import type { LucideIcon } from 'lucide-react';
import { BrainIcon, CodeIcon, WrenchIcon, ZapIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export type ReasoningState = 'streaming' | 'complete';
export type ToolInvocationState = 'input-streaming' | 'input-available' | 'output-available';

export type CoTReasoningStep = { type: 'reasoning'; text: string; title?: string; state?: ReasoningState };
export type CoTToolStep = {
  type: 'tool';
  toolName: string;
  label: ReactNode;
  state?: ToolInvocationState;
  input?: unknown;
  output?: unknown;
};
export type CoTStep = CoTReasoningStep | CoTToolStep;

export function getCoTStepStatus(step: CoTStep): 'active' | 'complete' {
  if (step.type === 'reasoning') {
    return step.state === 'streaming' ? 'active' : 'complete';
  }
  return step.state === 'output-available' ? 'complete' : 'active';
}

/** Strip leading **Title** from reasoning text to avoid duplicating the step label. */
export function stripLeadingTitle(text: string): string {
  return text.replace(/^\*\*(.+?)\*\*\s*\n?/, '').trim();
}

export const TOOL_LABELS: Record<string, string> = {
  checkCapability: 'Thinking...',
  coverageGapAnalysis: 'Analyzing coverage gaps...',
  dataExplorer: 'Exploring data...',
  image_generation: 'Generating image...',
  web_search: 'Searching the web...',
  processFiles: 'Processing files...',
  summarizeEntities: 'Summarizing...',
  fillSl1Form: 'Filling SL1 form...',
  fillSl2Form: 'Filling SL2 form...',
  formFilling: 'Filling form...',
  pptxRevisor: 'Revising content...',
  documentSearch: 'Searching documents...',
  readFile: 'Reading file...',
  getMessage: 'Reading email...',
  downloadEmailAttachments: 'Downloading attachments...',
  listInbox: 'Searching inbox...',
  listUnifiedMail: 'Searching mail...',
  loadSkill: 'Loading skill...',
  shell: 'Running command...',
};

export function getToolLabel(toolName: string, input?: unknown): ReactNode {
  if (toolName === 'loadSkill') {
    const skillId = getField<string>(input, 'skillId');
    if (skillId) {
      return (
        <>
          Using <code className="bg-muted rounded px-1 py-0.5 text-xs">{skillId}</code> skill
        </>
      );
    }
    return TOOL_LABELS.loadSkill;
  }
  if (toolName === 'shell') {
    const commands = getField<string[]>(input, 'action', 'commands');
    if (commands && commands.length > 1) {
      return <>Running {commands.length} commands</>;
    }
    return TOOL_LABELS.shell;
  }
  return TOOL_LABELS[toolName] ?? `Running ${toolName}...`;
}

export function getCoTStepIcon(step: CoTStep): LucideIcon {
  if (step.type === 'reasoning') return BrainIcon;
  if (step.toolName === 'loadSkill') return ZapIcon;
  if (step.toolName === 'shell') return CodeIcon;
  return WrenchIcon;
}

export function getCoTStepLabel(step: CoTStep): ReactNode {
  if (step.type === 'reasoning') return step.title || 'Thinking...';
  return step.label;
}

export function getCoTStepKey(step: CoTStep, index: number): string {
  if (step.type === 'reasoning') return `reasoning-${index}`;
  return `tool-${step.toolName}-${index}`;
}

export const MAX_OUTPUT_LENGTH = 500;

export type CoTSearchResult = { title: string; link?: string };

/** Safely retrieve a nested value from an unknown object. Returns undefined if the path doesn't match. */
export function getField<T>(obj: unknown, ...keys: string[]): T | undefined {
  const result = keys.reduce<unknown>(
    (current, key) =>
      current != null && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined,
    obj,
  );
  return result as T | undefined;
}
